import { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ZoomOut, RotateCw, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { openModal, closeModal } from '../../utils/modalUtils';

/**
 * Creates a cropped image from the source image and crop area
 */
const createCroppedImage = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  // Set canvas size to the crop dimensions
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped area
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Return as blob
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          // Generate unique filename with timestamp and random string
          const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
          const filename = `image-${uniqueId}.jpg`;
          
          const file = new File([blob], filename, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(file);
        } else {
          resolve(null);
        }
      },
      'image/jpeg',
      0.9
    );
  });
};

/**
 * Helper to load an image
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

/**
 * ImageCropper - Modal component for cropping uploaded images
 */
const ImageCropper = ({ 
  imageSrc, 
  onCropComplete, 
  onCancel,
  aspectRatio = 4 / 3 
}) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const scrollYRef = useRef(0);

  // Lock body scroll when cropper is open
  useEffect(() => {
    scrollYRef.current = openModal();
    return () => {
      closeModal(scrollYRef.current);
    };
  }, []);

  const onCropChange = useCallback((newCrop) => {
    setCrop(newCrop);
  }, []);

  const onZoomChange = useCallback((newZoom) => {
    setZoom(newZoom);
  }, []);

  const handleCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    
    setIsProcessing(true);
    try {
      const croppedImage = await createCroppedImage(imageSrc, croppedAreaPixels);
      if (croppedImage) {
        onCropComplete(croppedImage);
      }
    } catch (error) {
      console.error('Error cropping image:', error);
    }
    setIsProcessing(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">
              {t('crop_image', 'Crop Image')}
            </h3>
            <button
              type="button"
              onClick={onCancel}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              aria-label={t('cancel', 'Cancel')}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Cropper Area */}
          <div className="relative w-full aspect-[4/3] bg-gray-900">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspectRatio}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={handleCropComplete}
              cropShape="rect"
              showGrid={true}
              classes={{
                containerClassName: 'rounded-none',
              }}
            />
          </div>

          {/* Instructions */}
          <p className="text-center text-xs text-gray-500 py-2 bg-gray-50 border-t border-gray-100">
            {t('crop_instructions', 'Drag to position, pinch or use slider to zoom')}
          </p>

          {/* Controls */}
          <div className="px-4 py-3 bg-white border-t border-gray-200">
            {/* Zoom Slider */}
            <div className="flex items-center gap-3 mb-4">
              <ZoomOut className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-full appearance-none cursor-pointer
                           [&::-webkit-slider-thumb]:appearance-none
                           [&::-webkit-slider-thumb]:w-4
                           [&::-webkit-slider-thumb]:h-4
                           [&::-webkit-slider-thumb]:rounded-full
                           [&::-webkit-slider-thumb]:bg-primary
                           [&::-webkit-slider-thumb]:cursor-pointer
                           [&::-webkit-slider-thumb]:shadow-md"
              />
              <ZoomIn className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={handleRotate}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl
                           border-2 border-gray-200 text-gray-600 font-medium text-xs sm:text-sm
                           hover:bg-gray-50 transition-colors flex-shrink-0"
              >
                <RotateCw className="w-4 h-4" />
                <span className="hidden sm:inline">{t('rotate', 'Rotate')}</span>
              </button>
              
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl
                           bg-primary text-white font-medium text-xs sm:text-sm
                           hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {isProcessing ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {t('confirm', 'Confirm')}
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-3 py-2.5 rounded-xl border-2 border-gray-200
                           text-gray-600 font-medium text-xs sm:text-sm hover:bg-gray-50 transition-colors"
              >
                {t('cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImageCropper;

